import React, { useState } from "react";
import {
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";

import TableViewShell from "../../../../components/views/table-view-shell";
import { User } from "../../../../types";
import { TeamSummary } from "../../../../types.new";
import { UserAvatar } from "../../../../components/avatars";
import TextCopyChip from "../../../../components/chips/text-copy-chip";
import EditableRoleChip from "../../../../components/chips/editable-role-chip";
import RoleChip, {
  type RoleChipValue,
} from "../../../../components/chips/role-chip";
import { getTeamRoleInfo } from "../../../../utils/get-team-role-info";

type UserSortField = "name" | "email" | "teamRole";

interface UsersTableProps {
  team: TeamSummary;
  filteredUsers: User[];
  unfilteredUsersCount: number;
  canEdit: boolean;
}

const TEAM_ROLE_SORT_ORDER: RoleChipValue[] = [
  "Client Admin",
  "Team Admin",
  "Member",
  "Past Member",
  "Guest",
  "Blocked",
  "Developer Admin",
  "User",
];

const getRoleSortRank = (role: RoleChipValue) => {
  const rank = TEAM_ROLE_SORT_ORDER.indexOf(role);
  return rank === -1 ? TEAM_ROLE_SORT_ORDER.length : rank;
};

const UsersTable: React.FC<UsersTableProps> = ({
  team,
  filteredUsers,
  unfilteredUsersCount,
  canEdit,
}) => {
  const [sortBy, setSortBy] = useState<UserSortField>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const headerLabelSx = { fontSize: "0.875rem" };
  const headerCellSx = { py: 0.5, fontSize: "0.875rem", whiteSpace: "nowrap" };

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (sortBy === "email") {
      const comparison = (a.email || "").localeCompare(b.email || "", undefined, {
        sensitivity: "base",
      });

      return sortOrder === "asc" ? comparison : -comparison;
    }

    if (sortBy === "teamRole") {
      const aRole = getTeamRoleInfo(a, team.teamId);
      const bRole = getTeamRoleInfo(b, team.teamId);
      const rankComparison = getRoleSortRank(aRole) - getRoleSortRank(bRole);

      if (rankComparison !== 0) {
        return sortOrder === "asc" ? rankComparison : -rankComparison;
      }
    }

    const comparison = (a.fullName || a.email || "").localeCompare(
      b.fullName || b.email || "",
      undefined,
      { sensitivity: "base" },
    );

    return sortOrder === "asc" ? comparison : -comparison;
  });

  const handleSort = (field: UserSortField) => {
    if (sortBy === field) {
      setSortOrder((currentOrder) =>
        currentOrder === "asc" ? "desc" : "asc",
      );
      return;
    }

    setSortBy(field);
    setSortOrder("asc");
  };

  const columns = (
    <colgroup>
      <col style={{ width: 260 }} />
      <col style={{ width: 320 }} />
      <col style={{ width: 180 }} />
    </colgroup>
  );

  return (
    <TableViewShell
      shownCount={sortedUsers.length}
      totalCount={unfilteredUsersCount}
      columns={columns}
      headerAriaLabel="team members table header"
      bodyAriaLabel="team members table"
      header={
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx}>
              <TableSortLabel
                active={sortBy === "name"}
                direction={sortBy === "name" ? sortOrder : "asc"}
                onClick={() => handleSort("name")}
                sx={headerLabelSx}
              >
                Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headerCellSx}>
              <TableSortLabel
                active={sortBy === "email"}
                direction={sortBy === "email" ? sortOrder : "asc"}
                onClick={() => handleSort("email")}
                sx={headerLabelSx}
              >
                Email
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={headerCellSx}>
              <TableSortLabel
                active={sortBy === "teamRole"}
                direction={sortBy === "teamRole" ? sortOrder : "asc"}
                onClick={() => handleSort("teamRole")}
                sx={headerLabelSx}
              >
                Team Role
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
      }
      body={
        <TableBody>
          {sortedUsers.map((user) => {
            const role: RoleChipValue = getTeamRoleInfo(user, team.teamId);

            return (
              <TableRow key={user.userId}>
                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
                    <UserAvatar text={user.fullName || user.email || "Guest"} />
                    <Stack minWidth={0}>
                      <Typography noWrap fontWeight={600}>
                        {user.fullName || user.email || "Guest"}
                      </Typography>
                      {user.jobTitle ? (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {user.jobTitle}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Stack>
                </TableCell>
                <TableCell>
                  {user.email ? (
                    <TextCopyChip text={user.email} />
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      No email
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {canEdit && role !== "Client Admin" ? (
                    <EditableRoleChip
                      user={user}
                      team={team}
                      chipSize="small"
                      chipHeight="24px"
                      chipFontSize="0.7rem"
                    />
                  ) : (
                    <RoleChip
                      role={role}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: "0.7rem",
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      }
    />
  );
};

export default UsersTable;
